import React from 'react';

import DownloadIcon from '@atlaskit/icon/core/download';
import { Box, Text, xcss } from '@atlaskit/primitives';
import Anchor from '@atlaskit/primitives/anchor';
import { media } from '@atlaskit/primitives/responsive';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import ThemedImg from '../themed-img';

const cardStyles = xcss({
	gridColumn: 'span 12',
	[media.above.xs]: {
		gridColumn: 'span 6',
	},
	textDecoration: 'none',
	display: 'block',
	backgroundColor: 'color.background.neutral',

	':hover': {
		backgroundColor: 'color.background.neutral.hovered',
		textDecoration: 'none',
	},
	':active': {
		backgroundColor: 'color.background.neutral.pressed',
	},
});

const headerStyles = xcss({
	paddingBlock: 'space.250',
	paddingInline: 'space.200',
	backgroundColor: 'color.background.neutral',
	overflow: 'hidden',
	color: 'color.text',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '20px',
	borderStartStartRadius: 'radius.small',
	borderStartEndRadius: 'radius.small',
});

const contentStyles = xcss({
	padding: 'space.200',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	transition: 'background-color 0.2s ease-in',
	borderEndStartRadius: 'radius.small',
	borderEndEndRadius: 'radius.small',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	img: {
		maxHeight: '70px',
		maxWidth: '75%',
	},

	[media.above.xs]: {
		height: '11rem',

		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		img: {
			maxHeight: '100%',
			maxWidth: '100%',
		},
	},
});

interface AssetCardProps {
	title: string;
	link: string;
	fileSize: number;
	thumbnail: { light: string; dark: string };
}

// TODO: Fill in the component {description} and ensure links point to the correct {packageName} location.
// Remove links that the component does not have (such as usage). If there are no links remove them all.
/**
 * __Asset card__
 *
 * An asset card {description}.
 *
 * - [Examples](https://atlassian.design/components/{packageName}/examples)
 * - [Code](https://atlassian.design/components/{packageName}/code)
 * - [Usage](https://atlassian.design/components/{packageName}/usage)
 */
const AssetCard = ({ title, link, fileSize, thumbnail }: AssetCardProps): React.JSX.Element => {
	const KB = fileSize / 1024;
	const titleWithFileSize = `${title} (${(KB > 1000 ? KB / 1024 : KB).toFixed(2)}KB)`;

	return (
		<Anchor xcss={cardStyles} href={link} target="_blank">
			<VisuallyHidden>Download {titleWithFileSize}</VisuallyHidden>
			<Box xcss={headerStyles}>
				<Text maxLines={1}>{titleWithFileSize}</Text>
				<DownloadIcon label="" spacing="spacious" />
			</Box>
			<Box xcss={contentStyles}>
				<ThemedImg
					src={{
						light: thumbnail.light,
						dark: thumbnail.dark,
					}}
					alt=""
				/>
			</Box>
		</Anchor>
	);
};

export default AssetCard;
